/**
 * **The playhead of one media element, and who may move it** — the video and the audio player's
 * one implementation of a publication's `seeking` (`docs/plans/adc-rust.md` AD8).
 *
 * Two kinds of move reach the element. **The player's own** — a start offset, the trim window, its
 * resume point, the command of the page that framed it, the viewer's moves `seeking` allows — go
 * through `place`. **The ones no control of ours made** — iOS's fullscreen player, a
 * picture-in-picture window's scrubber, Firefox's « Show controls » — move the element directly,
 * and `onSeeking` puts them back while the viewer may not seek (`../seekGuard.ts`).
 *
 * The player wires the element's events to the four handlers; nothing here binds a listener, so a
 * player keeps one place where its element's events are bound and unbound.
 */

import { computed, type ComputedRef } from 'vue'
import { createSeekGuard, type SeekSpan } from '../seekGuard'

export interface UsePlayheadOptions {
  /** The element whose playhead this is, once it is mounted. */
  element: () => HTMLMediaElement | null
  /** Whether the viewer may move the playhead — the publication's `seeking`, `true` when absent. */
  canSeek?: () => boolean
  /** The span the playhead lives in: the media's, or a playlist entry's trim window. */
  span: () => SeekSpan
}

export interface UsePlayheadReturn {
  /** Whether the viewer may move the playhead, read on every move. */
  canSeek: ComputedRef<boolean>
  /** Move the playhead to `time` for the player itself: the move stands whatever `seeking` says. */
  place: (time: number) => void
  /** The pipeline is about to move the playhead to `time` for the player (hls.js's start position). */
  permit: (time: number) => void
  /** The element's `loadstart`: a new media begins at its start. */
  onLoadStart: () => void
  /** The element's `seeking`: a move landed, which is put back if the viewer may not make it. */
  onSeeking: () => void
  /** The element's `seeked`. */
  onSeeked: () => void
  /** The element's `timeupdate`: playback reached where the playhead is. */
  onTimeUpdate: () => void
}

export function usePlayhead(options: UsePlayheadOptions): UsePlayheadReturn {
  const canSeek = computed(() => options.canSeek?.() ?? true)
  const guard = createSeekGuard(() => canSeek.value)
  let moving = false

  function place(time: number): void {
    const element = options.element()
    if (!element) return
    guard.permit(time)
    element.currentTime = time
  }

  function onSeeking(): void {
    moving = true
    const element = options.element()
    if (!element) return
    const back = guard.landed(element.currentTime, options.span())
    if (back !== null) place(back)
  }

  function onTimeUpdate(): void {
    const element = options.element()
    if (element && !moving) guard.played(element.currentTime)
  }

  return {
    canSeek,
    place,
    permit: guard.permit,
    onLoadStart: () => {
      // A source change aborts a move under way and fires no `seeked` for it. Left set, the flag
      // would keep every `timeupdate` of the new media from counting, and `reached` would stay at
      // its start: the first step hls.js takes over a hole would be put back there.
      moving = false
      guard.reset()
    },
    onSeeking,
    onSeeked: () => {
      moving = false
    },
    onTimeUpdate,
  }
}
